<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'COI Header' }}</h2>
      <p>The issuer / header block printed on the Certificate of Insurance. Blank fields fall back to the member's policy data.</p>
    </div>
    <p-button label="Add Header" icon="pi pi-plus" (onClick)="openAdd()" />
  </div>

  <div class="card">
    <div class="toolbar">
      <div class="filter">
        <label>Client</label>
        <p-select
          [ngModel]="filterClientId()"
          (ngModelChange)="onFilterChange($event)"
          [options]="clientOptions()"
          optionValue="value"
          optionLabel="label"
          [showClear]="true"
          [filter]="true"
          placeholder="All clients"
          appendTo="body"
          styleClass="filter-select" />
      </div>
      <span class="count">{{ visible().length }} of {{ rows().length }}</span>
    </div>

    <p-table [value]="visible()" [loading]="loading()" dataKey="id" [rows]="10"
      [paginator]="visible().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm"
      [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
      <ng-template pTemplate="header">
        <tr>
          <th style="width: 210px">Scope</th>
          <th>Insurer / Office</th>
          <th>Intermediary</th>
          <th style="width: 110px">Active</th>
          <th class="ta-r" style="width: 140px">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr>
          <td>
            @if (r.clientId == null) {
              <p-tag value="Main / Partner" severity="info" />
            } @else {
              <span class="client-scope">
                <p-tag [value]="r.masterPolicyId ? 'Master Policy' : 'Client'" [severity]="r.masterPolicyId ? 'warn' : 'secondary'" />
                <span class="client-name">{{ scopeLabel(r) }}</span>
              </span>
            }
          </td>
          <td>
            <div class="issuer-text">
              <span class="issuer-name">{{ r.mainCompany || '—' }}</span>
              @if (r.policyServingOffice || r.place) {
                <small class="issuer-sub">{{ r.policyServingOffice }}{{ r.policyServingOffice && r.place ? ' · ' : '' }}{{ r.place }}</small>
              }
            </div>
          </td>
          <td>
            @if (r.intermediaryName || r.intermediaryCode) {
              <span>{{ r.intermediaryName || '—' }}</span>
              @if (r.intermediaryCode) { <small class="issuer-sub mono">{{ r.intermediaryCode }}</small> }
            } @else { <span class="dash">—</span> }
          </td>
          <td>
            <span class="active-toggle" (click)="toggleActive(r)" pTooltip="Click to toggle">
              <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
                [severity]="r.isActive ? 'success' : 'secondary'" />
            </span>
          </td>
          <td class="ta-r actions">
            <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
              pTooltip="Edit" (onClick)="openEdit(r)" />
            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
              pTooltip="Delete" (onClick)="remove(r)" />
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="5">
            <div class="state">
              <i class="pi pi-id-card"></i>
              @if (filterClientId() != null) {
                <p>No COI headers apply to this client yet.</p>
              } @else {
                <p>No COI headers yet. Add the <strong>Main / Partner</strong> header to get started.</p>
              }
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editing() ? 'Edit COI Header' : 'New COI Header'" styleClass="menu-dialog"
  [style]="{ width: '680px' }" [breakpoints]="{ '700px': '94vw' }">
  <form [formGroup]="form" class="menu-form">
    <div class="field">
      <label>Scope</label>
      <p-select [ngModel]="scopeMode()" (ngModelChange)="onScopeChange($event)"
        [ngModelOptions]="{ standalone: true }" [options]="scopeOptions" optionValue="value"
        optionLabel="label" [disabled]="clientLocked()" appendTo="body" />
      <small class="hint-muted">Main / Partner applies to all partners; a client header overrides it for that client; a client + master policy header overrides the client one for members under that policy.</small>
    </div>

    @if (scopeMode() === 'CLIENT') {
      <div class="field">
        <label>Client <span class="req">*</span></label>
        <p-select formControlName="clientId" [options]="clientOptions()" optionValue="value"
          optionLabel="label" [filter]="true" placeholder="Select a client" appendTo="body" />
        @if (clientLocked()) {
          <small class="hint-muted">Set from the premium-rule assignment — this client must have its own certificate header.</small>
        }
      </div>

      <div class="field">
        <label>Master Policy</label>
        <p-select formControlName="masterPolicyId" [options]="masterPolicyOptions()" optionValue="value"
          optionLabel="label" [filter]="true" [showClear]="true"
          placeholder="All master policies (client-wide)" emptyMessage="No master policies for this client"
          appendTo="body" />
        <small class="hint-muted">Leave blank for a client-wide header. Pick a master policy to override it for members issued under that policy only.</small>
      </div>
    }

    <p class="section-note"><i class="pi pi-info-circle"></i> Leave a field blank to use the member's policy value automatically.</p>

    <div class="grid-2">
      <div class="field">
        <label>Insurance Company (Insurer)</label>
        <input pInputText type="text" formControlName="mainCompany" placeholder="Generali Central Insurance Company Limited" autocomplete="off">
      </div>
      <div class="field">
        <label>Insured / Issued to (Partner Name)</label>
        <input pInputText type="text" formControlName="insuranceCompany" placeholder="e.g. ABC Finance Ltd" autocomplete="off">
      </div>
    </div>

    <div class="field">
      <label>Policy Servicing Office</label>
      <textarea pTextarea formControlName="policyServingOffice" rows="2" placeholder="Unit No.801 & 802, Tower C, …"></textarea>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Master Policy No @if (clientLocked()) { <span class="req">*</span> }</label>
        <input pInputText type="text" formControlName="masterPolicyNo" placeholder="Blank = member's master policy no" autocomplete="off">
      </div>
      <div class="field">
        <label>Certificate No</label>
        <input pInputText type="text" formControlName="certificateNo" placeholder="Blank = member's policy no (dynamic)" autocomplete="off">
      </div>
    </div>

    <div class="field">
      <label>Insured Name</label>
      <input pInputText type="text" formControlName="insuredName" placeholder="Blank = master policy name" autocomplete="off">
    </div>

    <div class="field">
      <label>Insured Address</label>
      <textarea pTextarea formControlName="insuredAddress" rows="2" placeholder="Blank = the client's address"></textarea>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Intermediary Name</label>
        <input pInputText type="text" formControlName="intermediaryName" placeholder="Blank = partner name" autocomplete="off">
      </div>
      <div class="field">
        <label>Intermediary Code</label>
        <input pInputText type="text" formControlName="intermediaryCode" placeholder="Blank = partner code" autocomplete="off">
      </div>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Period of Insurance @if (clientLocked()) { <span class="req">*</span> }</label>
        <input pInputText type="text" formControlName="periodofInsurance" placeholder="Blank = policy start–end dates (dynamic)" autocomplete="off">
      </div>
      <div class="field">
        <label>Place</label>
        <input pInputText type="text" formControlName="place" placeholder="Mumbai" autocomplete="off">
      </div>
    </div>

    <div class="field">
      <label>Remarks</label>
      <textarea pTextarea formControlName="remarks" rows="2" placeholder="Internal notes (not printed)"></textarea>
    </div>

    <label class="switch-row">
      <p-toggleSwitch formControlName="isActive" />
      <span>Active</span>
    </label>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
      <p-button label="Save" (onClick)="save()" [loading]="saving()" />
    </div>
  </form>
</p-dialog>
